import type { IObservableArray } from 'mobx';
import { useMemo } from 'react';

import type { Campaign, DisplayCreative } from '@feathr/blackbox';
import type { TChannel } from '@feathr/extender/components/Creatives/types';
import { integrationsToChannels } from '@feathr/extender/components/Creatives/types';
import useMetaIntegrationStatus from '@feathr/extender/hooks/useMetaIntegrationStatus';
import { useAccount } from '@feathr/extender/state';

export interface IUseChannelAvailabilityParams {
  campaign: Campaign;
  creatives?: IObservableArray<DisplayCreative>;
}

export interface IUseChannelAvailabilityResult {
  /** Channels selected on the campaign (from integrations) */
  selectedChannels: TChannel[] | undefined;
  /** Channels available for adding creatives (filters out meta for monetization, Lite tier, or no integration) */
  availableChannels: TChannel[] | undefined;
  /** Whether Display channel is selected */
  displaySelected: boolean;
  /** Whether Meta channel is selected */
  metaSelected: boolean;
  /** Whether any Display creatives exist */
  hasDisplayCreatives: boolean;
  /** Whether any Meta creatives exist */
  hasMetaCreatives: boolean;
  /** Whether the account is on Platform Lite tier */
  isPlatformLite: boolean;
  /** Whether the account has an existing Meta integration */
  hasExistingIntegration: boolean;
}

/**
 * Hook that centralizes all channel-related logic for a campaign.
 *
 * @param params - Campaign and optional creatives array
 * @returns Channel selection state and availability
 *
 * @example
 * const { availableChannels, hasDisplayCreatives } = useChannelAvailability({
 *   campaign,
 *   creatives: creatives.models,
 * });
 */
export function useChannelAvailability({
  campaign,
  creatives,
}: IUseChannelAvailabilityParams): IUseChannelAvailabilityResult {
  const account = useAccount();
  const { hasExistingIntegration } = useMetaIntegrationStatus();

  const { isOmniCampaign, isMonetization } = campaign;
  const integrations = campaign.get('integrations');
  const isPlatformLite = account.isPlatformLite;

  // Meta is unavailable if: monetization campaign, Lite tier, or no Meta integration set up
  const isMetaUnavailable = isMonetization || isPlatformLite || !hasExistingIntegration;

  const selectedChannels = useMemo(
    () => (isOmniCampaign ? integrationsToChannels(integrations) : undefined),
    [isOmniCampaign, integrations],
  );

  const availableChannels = useMemo(
    () => selectedChannels?.filter((ch) => !(ch === 'meta' && isMetaUnavailable)),
    [selectedChannels, isMetaUnavailable],
  );

  const displaySelected = selectedChannels?.includes('display') ?? false;
  const metaSelected = selectedChannels?.includes('meta') ?? false;

  // Include creatives?.length in dependencies to re-compute when creatives are loaded/changed
  const hasDisplayCreatives = useMemo(
    () => creatives?.some((c) => !c.isFacebook) ?? false,
    [creatives, creatives?.length],
  );

  const hasMetaCreatives = useMemo(
    () => creatives?.some((c) => c.isFacebook) ?? false,
    [creatives, creatives?.length],
  );

  return {
    selectedChannels,
    availableChannels,
    displaySelected,
    metaSelected,
    hasDisplayCreatives,
    hasMetaCreatives,
    isPlatformLite,
    hasExistingIntegration,
  };
}
